// SchrankViewer - Unified Read-Mode-Viewer
// Mobile-first: Stack-Layout. Navigation per Building/Floor-Selector.
// Räume klickbar → öffnet RoomDetailView mit modernen Schrank-Türen.
//
// `mode`:
//   'view'        → reiner Lesezugriff (alle Mitarbeiter)
//   'verwaltung'  → wie 'view', aber Klick auf Schrank öffnet AssignmentDialog
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ArrowLeft, ChevronRight, Lock, Building2, Layers,
  DoorOpen, Search, UserPlus, Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { schrankApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';
import { SchrankDoor, SchrankStatusBadge } from './SchrankDoor';
import { SchrankGroupRenderer } from './SchrankWall';
import { ContainerDetailModal } from './ContainerDetailModal';
import { MoebelSymbol } from './MoebelSymbol';
import { useSvgViewport } from './useSvgViewport';
import { AssignmentDialog } from './AssignmentDialog';

// =============================================================================
// Floor-Plan Viewer - Räume klickbar, kein Edit
// =============================================================================
function FloorPlanReadOnly({ floor, rooms, onRoomClick, schrankCounts, allSchraenke, moebelByRoom }) {
  const viewport = useSvgViewport({
    contentWidth: floor?.geometry?.width || 1200,
    contentHeight: floor?.geometry?.height || 800,
  });
  const [hoverId, setHoverId] = useState(null);

  const floorW = floor?.geometry?.width || 1200;
  const floorH = floor?.geometry?.height || 800;

  // Gruppiert Schränke nach Raum für die Mini-Schemen
  const schraenkeByRoom = React.useMemo(() => {
    const grouped = {};
    (allSchraenke || []).forEach(s => {
      (grouped[s.room_id] = grouped[s.room_id] || []).push(s);
    });
    return grouped;
  }, [allSchraenke]);

  if (!floor) return null;
  if (rooms.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <DoorOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Diese Etage hat noch keine Räume.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0 overflow-hidden">
        <div
          ref={viewport.containerRef}
          className={cn(
            'relative bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-900 dark:to-slate-800 select-none',
            viewport.isPanning ? 'cursor-grabbing' : viewport.spaceDown ? 'cursor-grab' : ''
          )}
          style={{ aspectRatio: `${floorW} / ${floorH}`, maxHeight: '70vh' }}
        >
          <svg
            ref={viewport.svgRef}
            viewBox={viewport.viewBoxString}
            width="100%" height="100%"
            onWheel={viewport.handleWheel}
            onPointerDown={viewport.handlePointerDown}
            style={{ display: 'block', touchAction: 'none' }}
          >
            {/* Floor boundary */}
            <rect
              x={0} y={0} width={floorW} height={floorH}
              fill="rgba(255,255,255,0.5)"
              stroke="#64748b" strokeWidth={2}
              rx={4}
            />
            {/* Rooms */}
            {rooms.map((r) => {
              const g = r.geometry;
              const hasSchraenke = r.hat_schraenke !== false;
              const count = schrankCounts?.[r.id];
              const fs = Math.min(g.width / 8, g.height / 8, 24);
              const isHover = hoverId === r.id && hasSchraenke;
              return (
                <g
                  key={r.id}
                  onClick={() => hasSchraenke && onRoomClick?.(r)}
                  onMouseEnter={() => hasSchraenke && setHoverId(r.id)}
                  onMouseLeave={() => setHoverId(null)}
                  style={{
                    cursor: hasSchraenke ? 'pointer' : 'default',
                    transition: 'transform 0.15s',
                  }}
                  data-testid={`view-room-${r.id}`}
                >
                  {/* Schatten */}
                  <rect
                    x={g.x + 2} y={g.y + 3} width={g.width} height={g.height}
                    fill="rgba(0,0,0,0.12)" rx={6}
                  />
                  {/* Body — Räume ohne Schränke: gestrichelt + ausgegraut */}
                  <rect
                    x={g.x} y={g.y} width={g.width} height={g.height}
                    fill={hasSchraenke ? (g.color || '#e2e8f0') : 'rgba(226,232,240,0.55)'}
                    stroke={isHover ? '#dc2626' : (hasSchraenke ? '#475569' : '#94a3b8')}
                    strokeWidth={isHover ? 2.5 : 1.5}
                    strokeDasharray={hasSchraenke ? undefined : '4 3'}
                    rx={6}
                    style={{ transition: 'all 0.15s' }}
                  />
                  {/* Highlight beim Hover */}
                  {isHover && (
                    <rect
                      x={g.x} y={g.y} width={g.width} height={g.height}
                      fill="rgba(220, 38, 38, 0.08)"
                      rx={6}
                      style={{ pointerEvents: 'none' }}
                    />
                  )}
                  {/* Mini-Schema der Schränke: positioniert wie im Editor
                      (relativ zur echten Raum-Geometrie skaliert) */}
                  {hasSchraenke && schraenkeByRoom[r.id] && schraenkeByRoom[r.id].length > 0 && (() => {
                    const list = schraenkeByRoom[r.id];
                    const roomW = g.width  || 1;
                    const roomH = g.height || 1;
                    // Raum-Innen-Koordinaten = Raum-Außen-Koordinaten
                    // (Schrank-Geometrie ist relativ zum Raum gespeichert,
                    // mit demselben Koordinatensystem wie r.geometry.width/height)
                    return (
                      <g style={{ pointerEvents: 'none', opacity: 0.85 }}>
                        {list.map((s) => {
                          const fillColor =
                            s.status === 'BELEGT' ? '#f97316' :
                            s.status === 'DEAKTIVIERT' ? '#94a3b8' :
                            s.status === 'RESERVIERT' ? '#eab308' :
                            '#10b981';
                          // Schrank-Geometrie relativ zum Raum.
                          // Falls Schrank-Koordinaten außerhalb [0, roomW/H] liegen,
                          // werden sie geklemmt, damit die Vorschau sauber bleibt.
                          const sx = Math.max(0, Math.min(roomW, s.geometry.x));
                          const sy = Math.max(0, Math.min(roomH, s.geometry.y));
                          const sw = Math.max(1, Math.min(roomW - sx, s.geometry.width));
                          const sh = Math.max(1, Math.min(roomH - sy, s.geometry.height));
                          return (
                            <rect
                              key={s.id}
                              x={g.x + sx}
                              y={g.y + sy}
                              width={sw}
                              height={sh}
                              fill={fillColor}
                              stroke="#1e293b"
                              strokeWidth={0.4}
                              opacity={0.9}
                            />
                          );
                        })}
                      </g>
                    );
                  })()}
                  {/* Raum-Name unten im Raum */}
                  <text
                    x={g.x + g.width / 2}
                    y={g.y + g.height * 0.65}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={fs}
                    fontWeight={hasSchraenke ? 700 : 500}
                    fill={hasSchraenke ? '#1e293b' : '#64748b'}
                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                  >
                    {r.name}
                  </text>
                  {hasSchraenke && count !== undefined && (
                    <text
                      x={g.x + g.width / 2}
                      y={g.y + g.height * 0.82}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={Math.max(fs * 0.6, 9)}
                      fill="#64748b"
                      style={{ pointerEvents: 'none', userSelect: 'none' }}
                    >
                      {count} Schränke
                    </text>
                  )}
                  {!hasSchraenke && r.raum_typ && (
                    <text
                      x={g.x + g.width / 2}
                      y={g.y + g.height * 0.82}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={Math.max(fs * 0.55, 8)}
                      fill="#94a3b8"
                      fontStyle="italic"
                      style={{ pointerEvents: 'none', userSelect: 'none' }}
                    >
                      {r.raum_typ}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Room Detail Viewer - moderne Schrank-Wand
// =============================================================================
function RoomDetailViewer({ room, schraenke, moebel, onBack, onSchrankClick, searchTerm, mode }) {
  const roomW = Math.round(room?.geometry?.width || 400);
  const roomH = Math.round(room?.geometry?.height || 300);
  const viewport = useSvgViewport({ contentWidth: roomW, contentHeight: roomH });
  // Hover-Tooltip-Status — zeigt Schrank-Info on-hover (kein Klick nötig)
  const [hoverInfo, setHoverInfo] = useState(null);
  const hoverTimerRef = useRef(null);

  const handleHoverEnter = useCallback((schrank, e) => {
    if (hoverTimerRef.current) { clearTimeout(hoverTimerRef.current); hoverTimerRef.current = null; }
    const rect = viewport.containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setHoverInfo({
      schrank,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  }, [viewport.containerRef]);
  const handleHoverLeave = useCallback(() => {
    // Kurze Verzögerung, damit der Tooltip nicht beim Wechsel zwischen Fächern flackert
    hoverTimerRef.current = setTimeout(() => setHoverInfo(null), 80);
  }, []);
  useEffect(() => () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
  }, []);

  // Filter (für Suche): hebt passende Schränke hervor, blendet andere ab
  const matchSet = useMemo(() => {
    const q = (searchTerm || '').trim().toLowerCase();
    if (!q) return null;
    const s = new Set();
    schraenke.forEach((sch) => {
      const hay = [
        sch.name, sch.mitarbeiter_name, sch.abteilung_name, sch.custom_typ,
      ].filter(Boolean).join(' ').toLowerCase();
      if (hay.includes(q)) s.add(sch.id);
    });
    return s;
  }, [searchTerm, schraenke]);

  const statusCounts = useMemo(() => {
    const c = { FREI: 0, BELEGT: 0, DEAKTIVIERT: 0, RESERVIERT: 0 };
    schraenke.forEach(s => { c[s.status] = (c[s.status] || 0) + 1; });
    return c;
  }, [schraenke]);

  return (
    <Card>
      <CardContent className="p-0">
        {/* Sub-Header mit Statuszählung */}
        <div className="flex flex-wrap items-center gap-3 px-3 py-2 border-b bg-muted/30">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
          </Button>
          <span className="font-semibold text-sm">{room.name}</span>
          <Badge variant="secondary">{schraenke.length} Schränke</Badge>
          <div className="ml-auto flex flex-wrap items-center gap-3">
            {Object.keys(statusCounts).map((s) => (
              <SchrankStatusBadge key={s} status={s} count={statusCounts[s]} />
            ))}
          </div>
        </div>

        {/* Schrank-Wand (SVG) */}
        <div
          ref={viewport.containerRef}
          className={cn(
            'relative select-none',
            // moderne Wand-Optik: Beton/Stein-Hintergrund
            'bg-gradient-to-b from-slate-200 to-slate-300 dark:from-slate-800 dark:to-slate-900',
            viewport.isPanning ? 'cursor-grabbing' : viewport.spaceDown ? 'cursor-grab' : ''
          )}
          style={{ aspectRatio: `${roomW} / ${roomH}`, maxHeight: '70vh' }}
        >
          <svg
            ref={viewport.svgRef}
            viewBox={viewport.viewBoxString}
            width="100%" height="100%"
            onWheel={viewport.handleWheel}
            onPointerDown={viewport.handlePointerDown}
            style={{ display: 'block', touchAction: 'none' }}
          >
            <defs>
              <pattern id="wall-texture" width="40" height="40" patternUnits="userSpaceOnUse">
                <rect width="40" height="40" fill="rgba(0,0,0,0.02)" />
                <line x1="0" y1="0" x2="40" y2="0" stroke="rgba(0,0,0,0.04)" strokeWidth="1" />
                <line x1="0" y1="20" x2="40" y2="20" stroke="rgba(0,0,0,0.03)" strokeWidth="0.5" />
              </pattern>
              <linearGradient id="floor-grad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="rgba(0,0,0,0.15)" />
                <stop offset="100%" stopColor="rgba(0,0,0,0.05)" />
              </linearGradient>
            </defs>

            {/* Wand-Hintergrund */}
            <rect x={0} y={0} width={roomW} height={roomH * 0.9}
                  fill="url(#wall-texture)" />
            {/* Boden */}
            <rect x={0} y={roomH * 0.9} width={roomW} height={roomH * 0.1}
                  fill="url(#floor-grad)" />

            {/* Möbel - dezent im Hintergrund */}
            {(moebel || []).map((m) => (
              <MoebelSymbol key={m.id} moebel={m} />
            ))}

            {/* Schränke (gruppiert nach wand_id, Container als Block) */}
            <SchrankGroupRenderer
              schraenke={schraenke}
              onSchrankClick={onSchrankClick}
              onSchrankHoverEnter={handleHoverEnter}
              onSchrankHoverLeave={handleHoverLeave}
              highlightSet={matchSet}
            />
            {schraenke.length === 0 && (
              <text x={roomW / 2} y={roomH / 2}
                    textAnchor="middle" fontSize={Math.min(roomW / 20, 18)}
                    fill="#64748b">
                Keine Schränke in diesem Raum
              </text>
            )}
          </svg>

          {/* Hover-Tooltip: zeigt Status + Belegung sofort beim Drüberfahren */}
          {hoverInfo && (
            <div
              data-testid="schrank-hover-tooltip"
              className="pointer-events-none absolute z-30 rounded-md border bg-white dark:bg-slate-900 shadow-lg px-3 py-2 text-xs min-w-[160px] max-w-[240px]"
              style={{
                left: Math.min(hoverInfo.x + 14, (viewport.containerRef.current?.clientWidth || 9999) - 250),
                top: Math.max(hoverInfo.y - 8, 0),
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ backgroundColor:
                    hoverInfo.schrank.status === 'FREI' ? '#10b981'
                    : hoverInfo.schrank.status === 'BELEGT' ? '#f97316'
                    : hoverInfo.schrank.status === 'RESERVIERT' ? '#eab308'
                    : '#94a3b8' }}
                />
                <span className="font-semibold">{hoverInfo.schrank.name}</span>
                {hoverInfo.schrank.ist_container && (
                  <span className="ml-auto text-[10px] uppercase tracking-wide text-muted-foreground">
                    Schrankwand
                  </span>
                )}
              </div>
              <div className="text-muted-foreground">
                {hoverInfo.schrank.status === 'FREI' && 'Frei'}
                {hoverInfo.schrank.status === 'BELEGT' && 'Belegt'}
                {hoverInfo.schrank.status === 'DEAKTIVIERT' && 'Deaktiviert'}
                {hoverInfo.schrank.status === 'RESERVIERT' && 'Reserviert'}
              </div>
              {hoverInfo.schrank.mitarbeiter_name && (
                <div className="mt-1 font-medium text-slate-800 dark:text-slate-100">
                  {hoverInfo.schrank.mitarbeiter_name}
                </div>
              )}
              {!hoverInfo.schrank.mitarbeiter_name && hoverInfo.schrank.abteilung_name && (
                <div className="mt-1 font-medium text-slate-800 dark:text-slate-100">
                  {hoverInfo.schrank.abteilung_name}
                </div>
              )}
              {hoverInfo.schrank.ist_container && (
                <div className="mt-1 text-[11px] text-muted-foreground">
                  {(hoverInfo.schrank.geometry?.spalten || '?') }
                  {' × '}
                  {(hoverInfo.schrank.geometry?.reihen || '?')} Fächer · Klick zum Öffnen
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Summary-Karte mit Status-Aufschlüsselung + Drill-Down zu freien/belegten Räumen
// =============================================================================
function SchrankSummaryCard({ schraenke, rooms, schrankByRoom, onJumpToRoom }) {
  const [expanded, setExpanded] = useState(null); // 'FREI' | 'BELEGT' | null

  const stats = useMemo(() => {
    const c = { total: 0, FREI: 0, BELEGT: 0, DEAKTIVIERT: 0, RESERVIERT: 0 };
    schraenke.forEach(s => {
      c.total += 1;
      c[s.status] = (c[s.status] || 0) + 1;
    });
    return c;
  }, [schraenke]);

  // Pro Raum: Anzahl in Status X
  const roomsByStatus = useMemo(() => {
    const result = { FREI: [], BELEGT: [], DEAKTIVIERT: [], RESERVIERT: [] };
    rooms.forEach((r) => {
      const list = schrankByRoom[r.id] || [];
      if (!list.length) return;
      const counts = list.reduce((acc, s) => {
        acc[s.status] = (acc[s.status] || 0) + 1;
        return acc;
      }, {});
      Object.keys(result).forEach((st) => {
        if (counts[st]) {
          result[st].push({ room: r, count: counts[st], total: list.length });
        }
      });
    });
    return result;
  }, [rooms, schrankByRoom]);

  if (stats.total === 0) return null;

  const cards = [
    { key: 'FREI', label: 'Frei', color: '#10b981',
      text: 'text-emerald-700', ring: 'ring-emerald-400' },
    { key: 'BELEGT', label: 'Belegt', color: '#f97316',
      text: 'text-orange-700', ring: 'ring-orange-400' },
    { key: 'RESERVIERT', label: 'Reserviert', color: '#eab308',
      text: 'text-yellow-700', ring: 'ring-yellow-400' },
    { key: 'DEAKTIVIERT', label: 'Deaktiviert', color: '#94a3b8',
      text: 'text-slate-600', ring: 'ring-slate-400' },
  ];

  // Anteil pro Status für die Stacked Bar
  const segments = cards.map(c => ({
    ...c,
    count: stats[c.key],
    pct: stats.total > 0 ? (stats[c.key] / stats.total) * 100 : 0,
  })).filter(s => s.count > 0);

  return (
    <div
      data-testid="schrank-summary"
      className="rounded-xl border bg-card overflow-hidden"
    >
      <div className="flex flex-col md:flex-row md:items-stretch">
        {/* Gesamt-Zahl groß und prominent links */}
        <div className="flex items-center gap-4 px-5 py-4 md:py-5 md:border-r md:min-w-[200px] bg-gradient-to-br from-slate-50 to-white dark:from-slate-900/60 dark:to-slate-900/30">
          <Lock className="w-7 h-7 text-muted-foreground" />
          <div>
            <p className="text-3xl font-bold tabular-nums leading-none">{stats.total}</p>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">
              Schränke gesamt
            </p>
          </div>
        </div>

        {/* Rechte Seite: Stacked Bar + Status-Chips */}
        <div className="flex-1 px-5 py-4 space-y-3">
          {/* Stacked Status-Bar */}
          <div className="h-2.5 w-full rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden flex"
               role="img"
               aria-label={`Status-Verteilung: ${segments.map(s => `${s.count} ${s.label}`).join(', ')}`}>
            {segments.map((s) => (
              <div
                key={s.key}
                style={{ width: `${s.pct}%`, backgroundColor: s.color }}
                className="h-full transition-all"
                title={`${s.label}: ${s.count} (${Math.round(s.pct)}%)`}
              />
            ))}
          </div>

          {/* Status-Chips, klickbar als Filter */}
          <div className="flex flex-wrap gap-1.5">
            {cards.map((c) => {
              const count = stats[c.key];
              const pct = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;
              const active = expanded === c.key;
              const disabled = count === 0;
              return (
                <button
                  key={c.key}
                  onClick={() => !disabled && setExpanded(active ? null : c.key)}
                  disabled={disabled}
                  className={cn(
                    'group inline-flex items-center gap-2 px-3 py-1.5 rounded-full',
                    'border bg-white dark:bg-slate-900/50',
                    'text-sm transition-all',
                    !disabled && 'hover:border-slate-400 hover:shadow-sm cursor-pointer',
                    active && `ring-2 ring-offset-1 ${c.ring} border-transparent`,
                    disabled && 'opacity-40 cursor-not-allowed'
                  )}
                  data-testid={`summary-${c.key.toLowerCase()}`}
                >
                  <span className="w-2 h-2 rounded-full shrink-0"
                        style={{ background: c.color, boxShadow: `0 0 0 2px ${c.color}22` }} />
                  <span className={cn('font-medium', c.text)}>{c.label}</span>
                  <span className="tabular-nums font-semibold text-foreground">{count}</span>
                  <span className="text-[11px] text-muted-foreground tabular-nums">
                    {pct}%
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Drill-Down */}
      {expanded && roomsByStatus[expanded].length > 0 && (
        <div className="border-t px-3 py-2 space-y-0.5 bg-slate-50/50 dark:bg-slate-900/30"
             data-testid="summary-drilldown">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground px-2 pb-1">
            Räume mit „{cards.find(c => c.key === expanded)?.label}"-Schränken
          </p>
          {roomsByStatus[expanded].map(({ room, count, total }) => (
            <button
              key={room.id}
              onClick={() => onJumpToRoom?.(room.id)}
              className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-white dark:hover:bg-slate-800/60 text-left transition-colors"
              data-testid={`summary-room-${room.id}`}
            >
              <DoorOpen className="w-4 h-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{room.name}</p>
                {room.raum_typ && (
                  <p className="text-xs text-muted-foreground truncate">{room.raum_typ}</p>
                )}
              </div>
              <Badge variant="secondary" className="tabular-nums">
                {count} / {total}
              </Badge>
              <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Main Viewer
// =============================================================================
export function SchrankViewer({ mode = 'view', canAssign = false }) {
  const [permissions, setPermissions] = useState({ can_edit_grundriss: false, can_manage_zuweisung: false });
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [rooms, setRooms] = useState([]);
  // allTopLevel: nur Container + Solo-Schränke (für Raum-Plan)
  const [allTopLevel, setAllTopLevel] = useState([]);
  // allFaecher: alle echten Fächer (Container ausgenommen) - für Statistik
  const [allFaecher, setAllFaecher] = useState([]);
  const [topLevelByRoom, setTopLevelByRoom] = useState({});
  const [faecherByRoom, setFaecherByRoom] = useState({});
  const [moebelByRoom, setMoebelByRoom] = useState({});
  const [selectedBuildingId, setSelectedBuildingId] = useState(null);
  const [selectedFloorId, setSelectedFloorId] = useState(null);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [assignDialog, setAssignDialog] = useState({ open: false, schrank: null });
  const [containerModal, setContainerModal] = useState({ open: false, container: null });
  const [infoModal, setInfoModal] = useState({ open: false, schrank: null });

  // Initial Load
  useEffect(() => {
    const init = async () => {
      try {
        const [permRes, buildingsRes] = await Promise.all([
          schrankApi.getMyPermissions(),
          schrankApi.listBuildings(),
        ]);
        setPermissions(permRes.data);
        setBuildings(buildingsRes.data);
        if (buildingsRes.data.length > 0) {
          setSelectedBuildingId(buildingsRes.data[0].id);
        }
      } catch (e) {
        console.error(e);
        toast.error('Daten konnten nicht geladen werden');
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);

  // Floors laden
  useEffect(() => {
    if (!selectedBuildingId) {
      setFloors([]);
      return;
    }
    schrankApi.listFloors(selectedBuildingId).then((res) => {
      setFloors(res.data);
      if (res.data.length > 0) setSelectedFloorId(res.data[0].id);
      else setSelectedFloorId(null);
    });
  }, [selectedBuildingId]);

  // Rooms + Schränke + Möbel laden (alle parallel)
  const loadFloorData = useCallback(async (floorId) => {
    if (!floorId) {
      setRooms([]); setAllTopLevel([]); setAllFaecher([]);
      setTopLevelByRoom({}); setFaecherByRoom({}); setMoebelByRoom({});
      return;
    }
    try {
      const [roomsRes, topRes, allRes] = await Promise.all([
        schrankApi.listRooms(floorId),
        // Top-Level: Container + Solo für Raum-Plan
        schrankApi.listSchraenke({ floor_id: floorId, top_level: true }),
        // Alle Schränke der Etage für Statistik (Container herausfiltern später)
        schrankApi.listSchraenke({ floor_id: floorId }),
      ]);
      setRooms(roomsRes.data);
      setAllTopLevel(topRes.data);
      const echte = allRes.data.filter(s => !s.ist_container);
      setAllFaecher(echte);

      const groupTop = {};
      topRes.data.forEach(s => {
        (groupTop[s.room_id] = groupTop[s.room_id] || []).push(s);
      });
      setTopLevelByRoom(groupTop);

      const groupFaecher = {};
      echte.forEach(s => {
        (groupFaecher[s.room_id] = groupFaecher[s.room_id] || []).push(s);
      });
      setFaecherByRoom(groupFaecher);

      // Möbel pro Raum laden (parallel pro Raum)
      const moebelResults = await Promise.all(
        roomsRes.data.map(r =>
          schrankApi.listMoebel(r.id).then(m => [r.id, m.data]).catch(() => [r.id, []])
        )
      );
      setMoebelByRoom(Object.fromEntries(moebelResults));
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { loadFloorData(selectedFloorId); }, [selectedFloorId, loadFloorData]);

  // Refresh nach Zuweisung
  const refresh = useCallback(() => {
    loadFloorData(selectedFloorId);
  }, [selectedFloorId, loadFloorData]);

  const selectedFloor = floors.find(f => f.id === selectedFloorId);
  const selectedRoom = rooms.find(r => r.id === selectedRoomId);
  // Raum-Detail: Top-Level (Container + Solo) als anzeigbare Einheiten
  const topLevelOfRoom = selectedRoomId ? (topLevelByRoom[selectedRoomId] || []) : [];
  const moebelOfRoom = selectedRoomId ? (moebelByRoom[selectedRoomId] || []) : [];

  // Schrank-Counts pro Raum (zählt Top-Level für Mini-Schemen, ECHTE Fächer für "X Schränke" Label)
  const schrankCounts = useMemo(() => {
    const c = {};
    Object.entries(faecherByRoom).forEach(([rid, list]) => { c[rid] = list.length; });
    return c;
  }, [faecherByRoom]);

  // Volltextsuche: über echte Fächer + Container-Namen
  const globalMatches = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    if (!q || selectedRoomId) return [];
    const hayList = [...allFaecher, ...allTopLevel.filter(s => s.ist_container)];
    return hayList.filter(s => {
      const hay = [s.name, s.mitarbeiter_name, s.abteilung_name, s.custom_typ]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    }).slice(0, 20);
  }, [search, allFaecher, allTopLevel, selectedRoomId]);

  const handleSchrankClick = (s) => {
    if (s.ist_container) {
      // Container: Detail-Modal mit Fächern öffnen
      setContainerModal({ open: true, container: s });
      return;
    }
    if (mode === 'verwaltung' && canAssign) {
      setAssignDialog({ open: true, schrank: s });
    } else {
      // Info-Modal anstelle eines Toasts
      setInfoModal({ open: true, schrank: s });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (buildings.length === 0) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <Building2 className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground">
            Es wurden noch keine Gebäude angelegt.
          </p>
          {permissions.can_edit_grundriss && (
            <p className="text-xs text-muted-foreground mt-2">
              → Wechsle in die Raumverwaltung, um Gebäude und Etagen zu erstellen.
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="schrank-viewer">
      {/* Selector-Header: Mobile-first Stack */}
      <Card>
        <CardContent className="p-3 space-y-3">
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1.5">
                <Building2 className="w-3.5 h-3.5" /> Gebäude
              </label>
              <Select value={selectedBuildingId || ''} onValueChange={(v) => {
                setSelectedBuildingId(v);
                setSelectedRoomId(null);
              }}>
                <SelectTrigger data-testid="view-building-select">
                  <SelectValue placeholder="Gebäude wählen" />
                </SelectTrigger>
                <SelectContent>
                  {buildings.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1.5">
                <Layers className="w-3.5 h-3.5" /> Etage
              </label>
              {/* Etagen als horizontale Pill-Reihe (von oben nach unten sortiert: höchstes OG zuerst, dann EG, dann UG) */}
              <div className="flex flex-wrap gap-1.5" data-testid="view-floor-pills">
                {floors.length === 0 ? (
                  <span className="text-sm text-muted-foreground italic px-2 py-1">
                    {selectedBuildingId ? 'Keine Etagen verfügbar' : 'Erst Gebäude wählen'}
                  </span>
                ) : (
                  [...floors]
                    .sort((a, b) => (b.etage ?? 0) - (a.etage ?? 0))
                    .map((f) => {
                      const active = f.id === selectedFloorId;
                      const label = f.etage > 0 ? `${f.etage}. OG`
                                  : f.etage < 0 ? `${Math.abs(f.etage)}. UG`
                                  : 'EG';
                      return (
                        <button
                          key={f.id}
                          onClick={() => {
                            setSelectedFloorId(f.id);
                            setSelectedRoomId(null);
                          }}
                          className={cn(
                            'inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-all',
                            active
                              ? 'bg-red-600 text-white border-red-600 shadow-sm'
                              : 'bg-white dark:bg-slate-900/50 hover:border-slate-400 hover:shadow-sm'
                          )}
                          data-testid={`floor-pill-${f.id}`}
                        >
                          <span className={cn(
                            'inline-flex items-center justify-center min-w-[28px] h-5 px-1.5 rounded text-xs font-bold tabular-nums',
                            active ? 'bg-white/20' : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300'
                          )}>
                            {label}
                          </span>
                          <span className="font-medium">{f.name}</span>
                        </button>
                      );
                    })
                )}
              </div>
            </div>
          </div>

          {/* Suche */}
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suche nach Schrank, Mitarbeiter oder Abteilung..."
              className="pl-9"
              data-testid="view-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Globale Suchergebnisse (nur in Etagen-Ansicht) */}
      {globalMatches.length > 0 && !selectedRoomId && (
        <Card>
          <CardContent className="p-2 space-y-1">
            <p className="text-xs text-muted-foreground px-2 py-1">
              {globalMatches.length} Treffer
            </p>
            {globalMatches.map(s => {
              const room = rooms.find(r => r.id === s.room_id);
              return (
                <button
                  key={s.id}
                  onClick={() => {
                    if (room) setSelectedRoomId(room.id);
                  }}
                  className="w-full flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 text-left"
                  data-testid={`match-${s.id}`}
                >
                  <SchrankStatusBadge status={s.status} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{s.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {room?.name} · {s.mitarbeiter_name || s.abteilung_name || 'Frei'}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Etage oder Raum-Detail */}
      {!selectedRoomId ? (
        <>
          <SchrankSummaryCard
            schraenke={allFaecher}
            rooms={rooms}
            schrankByRoom={faecherByRoom}
            onJumpToRoom={(rid) => setSelectedRoomId(rid)}
          />
          <FloorPlanReadOnly
            floor={selectedFloor}
            rooms={rooms}
            onRoomClick={(r) => setSelectedRoomId(r.id)}
            schrankCounts={schrankCounts}
            allSchraenke={allTopLevel}
            moebelByRoom={moebelByRoom}
          />
        </>
      ) : (
        <RoomDetailViewer
          room={selectedRoom}
          schraenke={topLevelOfRoom}
          moebel={moebelOfRoom}
          onBack={() => setSelectedRoomId(null)}
          onSchrankClick={handleSchrankClick}
          searchTerm={search}
          mode={mode}
        />
      )}

      <AssignmentDialog
        open={assignDialog.open}
        onOpenChange={(open) => setAssignDialog((s) => ({ ...s, open }))}
        schrank={assignDialog.schrank}
        canManage={canAssign}
        onChanged={refresh}
      />

      <ContainerDetailModal
        open={containerModal.open}
        onOpenChange={(open) => setContainerModal((s) => ({ ...s, open }))}
        container={containerModal.container}
        canAssign={canAssign}
        onChanged={refresh}
        buildingName={buildings.find(b => b.id === selectedBuildingId)?.name}
        floorName={selectedFloor?.name}
        roomName={(rooms.find(r => r.id === containerModal.container?.room_id))?.name}
      />

      {/* Mini Info-Modal — zeigt Status und Belegung bei Klick auf Solo-Schrank */}
      <Dialog open={infoModal.open} onOpenChange={(open) => setInfoModal((s) => ({ ...s, open }))}>
        <DialogContent className="max-w-xs" data-testid="schrank-info-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-muted-foreground" />
              {infoModal.schrank?.name || ''}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Detail-Information zum Schrank
            </DialogDescription>
          </DialogHeader>
          {infoModal.schrank && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <SchrankStatusBadge status={infoModal.schrank.status} />
                {infoModal.schrank.custom_typ ? (
                  <Badge variant="outline" className="text-xs">{infoModal.schrank.custom_typ}</Badge>
                ) : null}
              </div>
              {infoModal.schrank.mitarbeiter_name && (
                <div className="rounded-md border bg-orange-50 dark:bg-orange-950/20 p-2.5">
                  <p className="text-xs text-muted-foreground mb-0.5">Belegt von</p>
                  <p className="font-semibold text-sm">{infoModal.schrank.mitarbeiter_name}</p>
                </div>
              )}
              {!infoModal.schrank.mitarbeiter_name && infoModal.schrank.abteilung_name && (
                <div className="rounded-md border bg-orange-50 dark:bg-orange-950/20 p-2.5">
                  <p className="text-xs text-muted-foreground mb-0.5">Belegt von Abteilung</p>
                  <p className="font-semibold text-sm">{infoModal.schrank.abteilung_name}</p>
                </div>
              )}
              {!infoModal.schrank.mitarbeiter_name && !infoModal.schrank.abteilung_name && (
                <p className="text-sm text-muted-foreground">
                  Dieser Schrank ist derzeit nicht zugewiesen.
                </p>
              )}
              {infoModal.schrank.notiz && (
                <div className="text-xs text-muted-foreground border-t pt-2">
                  <p className="font-medium mb-0.5">Notiz</p>
                  <p>{infoModal.schrank.notiz}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SchrankViewer;
